import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { clientesAPI, handleAPIError } from '../../services/api';

// Thunks para operaciones asíncronas
export const fetchCustomers = createAsyncThunk(
  'customers/fetchCustomers',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await clientesAPI.getAll(filters);
      return response.data;
    } catch (error) {
      return rejectWithValue(handleAPIError(error));
    }
  }
);

export const createCustomer = createAsyncThunk(
  'customers/createCustomer',
  async (customerData, { rejectWithValue }) => {
    try {
      const response = await clientesAPI.create(customerData);
      return response.data;
    } catch (error) {
      return rejectWithValue(handleAPIError(error));
    }
  }
);

export const updateCustomer = createAsyncThunk(
  'customers/updateCustomer',
  async ({ id, customerData }, { rejectWithValue }) => {
    try {
      const response = await clientesAPI.update(id, customerData);
      return response.data;
    } catch (error) {
      return rejectWithValue(handleAPIError(error));
    }
  }
);

export const deleteCustomer = createAsyncThunk(
  'customers/deleteCustomer',
  async (id, { rejectWithValue }) => {
    try {
      await clientesAPI.delete(id);
      return id;
    } catch (error) {
      return rejectWithValue(handleAPIError(error));
    }
  }
);

const initialState = {
  customers: [],
  loading: false,
  error: null,
  filters: {
    search: '',
    clientType: 'all' // 'all', 'exportacion', 'nacional'
  }
};

const customersSlice = createSlice({
  name: 'customers',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        search: '',
        clientType: 'all'
      };
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch customers
      .addCase(fetchCustomers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomers.fulfilled, (state, action) => {
        state.loading = false;
        state.customers = action.payload;
      })
      .addCase(fetchCustomers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create customer
      .addCase(createCustomer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCustomer.fulfilled, (state, action) => {
        state.loading = false;
        state.customers.push(action.payload);
      })
      .addCase(createCustomer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update customer
      .addCase(updateCustomer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCustomer.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.customers.findIndex(customer => customer.id === action.payload.id);
        if (index !== -1) {
          // Update with the full response including transportes relationship
          state.customers[index] = action.payload;
        }
      })
      .addCase(updateCustomer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete customer
      .addCase(deleteCustomer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCustomer.fulfilled, (state, action) => {
        state.loading = false;
        state.customers = state.customers.filter(customer => customer.id !== action.payload);
      })
      .addCase(deleteCustomer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const {
  setFilters,
  clearFilters,
  clearError
} = customersSlice.actions;

// Selectors
export const selectAllCustomers = (state) => state.customers.customers;
export const selectCustomersLoading = (state) => state.customers.loading;
export const selectCustomersError = (state) => state.customers.error;
export const selectCustomersFilters = (state) => state.customers.filters;

export const selectFilteredCustomers = (state) => {
  const customers = selectAllCustomers(state);
  const filters = selectCustomersFilters(state);
  
  return customers.filter(customer => {
    const matchesSearch = !filters.search || 
      customer.nombre?.toLowerCase().includes(filters.search.toLowerCase()) ||
      customer.nombre_contacto?.toLowerCase().includes(filters.search.toLowerCase()) ||
      customer.email_contacto?.toLowerCase().includes(filters.search.toLowerCase()) ||
      customer.codigo_cliente?.toLowerCase().includes(filters.search.toLowerCase());
    
    // Filter by client type based on country
    let matchesClientType = true;
    if (filters.clientType === 'exportacion') {
      // Exportación: USA or Canada
      matchesClientType = customer.pais === 'USA' || customer.pais === 'Canada';
    } else if (filters.clientType === 'nacional') {
      // Nacional: Chile
      matchesClientType = customer.pais === 'CL';
    }
    // 'all' matches everything
    
    return matchesSearch && matchesClientType;
  });
};

export default customersSlice.reducer;
